import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Paintbrush, Scissors, Wrench, RefreshCw, ChevronRight, FolderOpen } from 'lucide-react';

const TYPES = [
  { key: 'paint',  label: 'Paint Finish',  icon: Paintbrush },
  { key: 'fabric', label: 'Fabric Finish', icon: Scissors },
  { key: 'metal',  label: 'Metal Finish',  icon: Wrench },
];

const SHEET_BADGES = {
  submitted: 'bg-green-100 text-green-700 border-green-300',
  draft: 'bg-amber-100 text-amber-700 border-amber-300',
  none: 'bg-gray-100 text-gray-500 border-gray-300',
};

export default function FinishListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/worker') ? '/worker' : '/admin';
  const [type, setType] = useState('paint');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/finish-sheets');
      setData(res.data || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load finish sheets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data[type] || [];

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">Finish Sheets</h1>
          <p className="text-sm text-gray-500">Paint / Fabric / Metal finish details per Manufacturing Order — rows auto-populate from flagged PO items</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50" title="Refresh">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex gap-2">
        {TYPES.map(t => {
          const Icon = t.icon;
          const count = (data[t.key] || []).length;
          return (
            <button key={t.key} onClick={() => setType(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                type === t.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}>
              <Icon className="w-4 h-4" /> {t.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-gray-400 text-sm">
            No Manufacturing Orders have items flagged for {TYPES.find(t => t.key === type)?.label} yet.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <th className="px-4 py-3">PO No.</th>
                <th className="px-4 py-3">Project Name</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Linked Project</th>
                <th className="px-4 py-3 text-center">Flagged Items</th>
                <th className="px-4 py-3 text-center">{type === 'metal' ? 'Qty' : 'Finish Qty'}</th>
                <th className="px-4 py-3 text-center">Filled</th>
                <th className="px-4 py-3">Sheet Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => {
                const st = r.sheet_id ? r.sheet_status : 'none';
                return (
                  <tr key={r.main_po_id} className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`${basePath}/main-po/${r.main_po_id}/finish/${type}`)}>
                    <td className="px-4 py-3 font-semibold text-blue-700">{r.po_no}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{r.project_name}</td>
                    <td className="px-4 py-3 text-gray-600">{r.client_name || '—'}</td>
                    <td className="px-4 py-3">
                      {r.linked_project_code ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-semibold text-xs">
                          <FolderOpen className="w-3.5 h-3.5" /> {r.linked_project_code}
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">{r.flagged_items}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-semibold">{Number(r.finish_qty ?? r.total_qty ?? 0)}</span>
                      {type !== 'metal' && Number(r.finish_qty) < Number(r.total_qty) && <span className="text-xs text-gray-400"> of {Number(r.total_qty)}</span>}
                    </td>
                    <td className="px-4 py-3 text-center">{r.filled_entries || 0} / {r.flagged_items}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-semibold ${SHEET_BADGES[st]}`}>
                        {st === 'none' ? 'Not started' : st === 'draft' ? 'Draft' : 'Submitted'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="w-4 h-4 text-gray-400 inline" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
